/** Builds the reprice candidate + diff for App.js's startReprice/RepriceDialog
 *  flow (CONF-106 part 3, spec §2.5: "fetch the latest catalog, diff it
 *  against the session's frozen basis (with the exec's overrides re-applied
 *  on both sides), and let the exec accept/cancel").
 *
 *  Extracted out of App.js so the `asOf` threading below is unit-testable in
 *  isolation — App.js itself has no test coverage here (App.test.js fails on
 *  an unrelated module-resolution error and is out of scope for this task).
 *
 *  asOf: pass the LIVE edition's asOf (priceEditions.edition?.asOf), NOT the
 *  frozen basis's own `resolvedAsOf` — which it now generally DOES carry (see
 *  CatalogSnapshot.resolvedAsOf), which is exactly why that field is not named
 *  `asOf`: `basis.asOf` would read right here while being silently wrong.
 *  Reprice answers
 *  "what would this configuration cost against TODAY's published price
 *  book" — the exact same cutoff useExecPricing's own live fetch uses (see
 *  useExecPricing.js) — so the diff is always against a catalog state some
 *  published edition actually corresponds to. Using the basis's own original
 *  asOf would just diff the basis against itself (nothing to show); leaving
 *  it unscoped (undefined, "latest catalog") would reintroduce the exact
 *  "reset to catalog shows a price no rep can see" defect this task exists
 *  to close, just at the reprice seam instead of the initial-load one.
 */
import { snapshotFromRows } from './rowsToSnapshot';
import { computeRepriceDiff, RepriceDiff } from './repriceDiff';
import type { CatalogSnapshot } from './engine';
import type { PricingOverrides } from '../domain/PricingOverrides';
import type { CatalogComponentRow, CatalogCompositionRow } from './__fixtures__/catalogRows';

export type CatalogRowsFetch = (vendor: string) =>
  Promise<{ components: CatalogComponentRow[]; compositions: CatalogCompositionRow[] }>;

export async function buildRepriceCandidate(
  fetchCatalogRows: CatalogRowsFetch,
  basis: CatalogSnapshot,
  overrides: PricingOverrides,
  asOf: string | undefined,
): Promise<{ candidate: CatalogSnapshot; diff: RepriceDiff }> {
  const { components, compositions } = await fetchCatalogRows(basis.vendor);
  // Empty-resolution guard (an unseeded vendor, or an out-of-range quarter,
  // must fail loudly) lives in snapshotFromRows itself — see its doc.
  //
  // The cutoff the candidate is resolved against travels ON the candidate
  // (CatalogSnapshot.resolvedAsOf, set by snapshotFromRows from this very
  // argument) — this function no longer echoes it back beside it. The caller
  // (App.js's acceptReprice, possibly long after this resolves, once the exec
  // reviews the dialog) passes the candidate itself into execPricing.rebase(),
  // which reads candidate.resolvedAsOf rather than re-reading whatever the
  // live edition's asOf happens to be at accept time. If the edition
  // republishes while the dialog is open those two values differ — see
  // useExecPricing.js's rebase() for why that distinction matters.
  const candidate = snapshotFromRows(basis.vendor, components, compositions, basis.quarter, asOf);
  return { candidate, diff: computeRepriceDiff(basis, candidate, overrides) };
}
